/**
 * Production seat + bet-chip placement.
 * Same DO tables on phone + browser — only chrome/seat % change.
 *
 * Phone portrait SSOT: `/media/phone-poker-example.png` (phonepoker3)
 * — vertical oval, wood rail, cyan neon rim, navy felt, gold logo,
 *   opponents on brass-stud ring, hero avatar+stack on bottom rim,
 *   hole cards + betting dock BELOW felt (not a second table).
 */

export type SeatPos = { left: string; top: string; label: string };

/** 6-max landscape browser — seats outside wide oval */
export const SEAT_POS_6: SeatPos[] = [
  { left: "50%", top: "96%", label: "hero" },
  { left: "6%", top: "72%", label: "bottom-left" },
  { left: "4%", top: "36%", label: "mid-left" },
  { left: "22%", top: "4%", label: "top-left" },
  { left: "78%", top: "4%", label: "top-right" },
  { left: "96%", top: "36%", label: "mid-right" },
];

/**
 * 6-max phone portrait (phonepoker3 brass-stud ring).
 * Hero sits ON bottom rim (avatar + stack); cards/betting stay in dock below.
 */
export const SEAT_POS_6_V: SeatPos[] = [
  { left: "50%", top: "90%", label: "hero-rim" },
  { left: "14%", top: "74%", label: "lower-left" },
  { left: "6%", top: "40%", label: "mid-left" },
  { left: "50%", top: "6%", label: "top" },
  { left: "94%", top: "40%", label: "mid-right" },
  { left: "86%", top: "74%", label: "lower-right" },
];

/** HU landscape — WSOP-style: villain top rail, hero bottom rail */
export const SEAT_POS_2: SeatPos[] = [
  { left: "50%", top: "97%", label: "hero" },
  { left: "50%", top: "3%", label: "villain" },
];

/** HU phone — villain top of oval, hero bottom rim */
export const SEAT_POS_2_V: SeatPos[] = [
  { left: "50%", top: "90%", label: "hero-rim" },
  { left: "50%", top: "7%", label: "villain" },
];

export const SEAT_POS_9: SeatPos[] = [
  { left: "50%", top: "96%", label: "hero" },
  { left: "18%", top: "88%", label: "bottom-left" },
  { left: "82%", top: "88%", label: "bottom-right" },
  { left: "4%", top: "52%", label: "mid-left" },
  { left: "96%", top: "52%", label: "mid-right" },
  { left: "12%", top: "12%", label: "top-left" },
  { left: "50%", top: "2%", label: "top-center" },
  { left: "88%", top: "12%", label: "top-right" },
  { left: "32%", top: "92%", label: "near-hero" },
];

/** 9-max phone — denser brass-stud ring on tall oval */
export const SEAT_POS_9_V: SeatPos[] = [
  { left: "50%", top: "90%", label: "hero-rim" },
  { left: "16%", top: "78%", label: "lower-left" },
  { left: "84%", top: "78%", label: "lower-right" },
  { left: "5%", top: "50%", label: "mid-left" },
  { left: "95%", top: "50%", label: "mid-right" },
  { left: "16%", top: "18%", label: "upper-left" },
  { left: "50%", top: "5%", label: "top" },
  { left: "84%", top: "18%", label: "upper-right" },
  { left: "32%", top: "86%", label: "near-hero" },
];

export function seatLayoutFor(maxPlayers: number, vertical: boolean): SeatPos[] {
  if (maxPlayers <= 2) return vertical ? SEAT_POS_2_V : SEAT_POS_2;
  if (maxPlayers >= 9) return vertical ? SEAT_POS_9_V : SEAT_POS_9;
  return vertical ? SEAT_POS_6_V : SEAT_POS_6;
}

/**
 * Bet chip stack sits on the felt between seat and pot (not inside seat chrome).
 * Offset from seat center toward table center (50%, 50%).
 */
export function betChipOffset(
  seatLeftPct: number,
  seatTopPct: number,
  isHu: boolean,
): { left: string; top: string } {
  const cx = 50;
  const cy = isHu ? 48 : 50;
  // Pull ~38% of the way from seat toward pot
  const t = 0.38;
  const left = seatLeftPct + (cx - seatLeftPct) * t;
  const top = seatTopPct + (cy - seatTopPct) * t;
  return { left: `${left.toFixed(1)}%`, top: `${top.toFixed(1)}%` };
}

export function parsePct(s: string): number {
  return Number(String(s).replace("%", "")) || 50;
}
